import * as React from 'react';
import { createRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useFieldArray, useForm } from 'react-hook-form';
import { Plus, Trash2 } from 'lucide-react';
import type {
  DocType,
  DocumentCreateRequest,
  DocumentDetail,
  DocumentLineInput,
  GstReasonCode,
} from '@bits/shared/zod';
import {
  computeDocumentTotals,
  formatInr,
  isIntraState,
  paise,
  parseDocNumber,
  qtyMilli,
  rupeesToPaise,
  sum,
} from '@bits/domain';
import { rootRoute } from './__root';
import { AppShell } from '@/components/AppShell';
import { api } from '@/lib/api';
import { errorMessage as toMessage } from '@/lib/errors';
import { placeOfSupplyHint } from '@/components/GstinAssist';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Field } from '@/components/ui/field';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

export const billingDocumentNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/billing/documents/new',
  component: NewDocumentPage,
});

export const billingDocumentEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/billing/documents/$documentId/edit',
  component: EditDocumentPage,
});

const IST_OFFSET_MS = (5 * 60 + 30) * 60 * 1000;

function todayIso(): string {
  return new Date(Date.now() + IST_OFFSET_MS).toISOString().slice(0, 10);
}

function addDays(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return '';
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function daysBetween(from: string, to: string): number | null {
  const a = new Date(`${from}T00:00:00Z`).getTime();
  const b = new Date(`${to}T00:00:00Z`).getTime();
  if (Number.isNaN(a) || Number.isNaN(b)) return null;
  return Math.round((b - a) / 86_400_000);
}

type TermsKey = 'receipt' | '7' | '15' | '30' | '45' | '60' | 'custom';

const TERMS_OPTIONS: ReadonlyArray<{ value: TermsKey; label: string; days: number | null }> = [
  { value: 'receipt', label: 'Due on receipt', days: 0 },
  { value: '7', label: 'Due in 7 days', days: 7 },
  { value: '15', label: 'Due in 15 days', days: 15 },
  { value: '30', label: 'Due in 30 days', days: 30 },
  { value: '45', label: 'Due in 45 days', days: 45 },
  { value: '60', label: 'Due in 60 days', days: 60 },
  { value: 'custom', label: 'Custom date', days: null },
];

function termsForDays(days: number | null): TermsKey {
  if (days === null) return 'custom';
  const match = TERMS_OPTIONS.find((t) => t.days === days);
  return match ? match.value : 'custom';
}

function daysForTerms(key: TermsKey): number | null {
  return TERMS_OPTIONS.find((t) => t.value === key)?.days ?? null;
}

const DOC_TYPE_OPTIONS: ReadonlyArray<{ value: Exclude<DocType, 'credit_note'>; label: string }> = [
  { value: 'tax_invoice', label: 'Tax invoice' },
  { value: 'bill_of_supply', label: 'Bill of supply' },
  { value: 'receipt', label: 'Receipt' },
];

const GST_RATE_OPTIONS = [
  { label: '0%', value: 0 },
  { label: '5%', value: 500 },
  { label: '12%', value: 1200 },
  { label: '18%', value: 1800 },
  { label: '28%', value: 2800 },
];

const GST_REASON_OPTIONS: ReadonlyArray<{ label: string; value: GstReasonCode }> = [
  { label: 'Exempt', value: 'exempt' },
  { label: 'Nil-rated', value: 'nil_rated' },
  { label: 'Unregistered counterparty', value: 'unregistered_counterparty' },
  { label: 'Composition', value: 'composition' },
  { label: 'Non-supply · advance', value: 'non_supply_advance' },
  { label: 'Non-supply · reimbursement', value: 'non_supply_reimbursement' },
  { label: 'Non-supply · capital', value: 'non_supply_capital' },
];

const GST_TREATMENT_LABEL: Record<string, string> = {
  registered: 'Registered',
  unregistered: 'Unregistered',
  composition: 'Composition',
  exempt: 'Exempt',
};

const DEFAULT_CUSTOMER_NOTES = 'Thanks for your business.';
const DEFAULT_TERMS_TEXT =
  'Kindly transfer the above mentioned amount to the bank account shown under "Remit To" on this invoice.';

type LineForm = {
  description: string;
  hsnSac: string;
  qtyDecimal: number;
  rateRupees: number;
  gstRateBpp: number;
};

type EditorValues = {
  docType: DocType;
  clientId: string;
  projectId: string;
  issueDate: string;
  terms: TermsKey;
  dueDate: string;
  placeOfSupplyState: string;
  number: string;
  gstReasonCode: '' | GstReasonCode;
  customerNotes: string;
  termsText: string;
  lines: LineForm[];
};

const EMPTY_LINE: LineForm = {
  description: '',
  hsnSac: '',
  qtyDecimal: 1,
  rateRupees: 0,
  gstRateBpp: 1800,
};

function emptyValues(): EditorValues {
  const issueDate = todayIso();
  return {
    docType: 'tax_invoice',
    clientId: '',
    projectId: '',
    issueDate,
    terms: '30',
    dueDate: addDays(issueDate, 30),
    placeOfSupplyState: '',
    number: '',
    gstReasonCode: '',
    customerNotes: DEFAULT_CUSTOMER_NOTES,
    termsText: DEFAULT_TERMS_TEXT,
    lines: [{ ...EMPTY_LINE }],
  };
}

function valuesFromDraft(doc: DocumentDetail): EditorValues {
  const dueDate = doc.dueDate ?? '';
  return {
    docType: doc.docType,
    clientId: doc.clientId,
    projectId: doc.projectId ?? '',
    issueDate: doc.issueDate,
    terms: dueDate ? termsForDays(daysBetween(doc.issueDate, dueDate)) : 'custom',
    dueDate,
    placeOfSupplyState: doc.placeOfSupplyState,
    number: doc.requestedNumber ?? '',
    gstReasonCode: doc.gstReasonCode ?? '',
    customerNotes: doc.customerNotes ?? '',
    termsText: doc.termsText ?? '',
    lines: doc.lines.map((l) => ({
      description: l.description,
      hsnSac: l.hsnSac ?? '',
      qtyDecimal: l.qty / 1000,
      rateRupees: l.ratePaise / 100,
      gstRateBpp: l.igstPct > 0 ? l.igstPct : l.cgstPct + l.sgstPct,
    })),
  };
}

function toLineInput(l: LineForm): DocumentLineInput {
  const qtyM = Math.round(l.qtyDecimal * 1000);
  return {
    description: l.description,
    hsnSac: l.hsnSac.trim() || undefined,
    qty: qtyM > 0 ? qtyM : 1,
    ratePaise: Number(rupeesToPaise(l.rateRupees || 0)),
    gstRateBpp: l.gstRateBpp,
  };
}

function toRequest(v: EditorValues, nextInSeries: string | null): DocumentCreateRequest {
  const body: DocumentCreateRequest = {
    docType: v.docType,
    clientId: v.clientId,
    issueDate: v.issueDate,
    placeOfSupplyState: v.placeOfSupplyState.trim(),
    lines: v.lines.map(toLineInput),
  };
  const number = v.number.trim();
  if (number && number !== nextInSeries) body.number = number;
  if (v.projectId) body.projectId = v.projectId;
  if (v.dueDate) body.dueDate = v.dueDate;
  if (v.gstReasonCode) body.gstReasonCode = v.gstReasonCode;
  if (v.customerNotes.trim()) body.customerNotes = v.customerNotes.trim();
  if (v.termsText.trim()) body.termsText = v.termsText.trim();
  return body;
}

function NewDocumentPage(): JSX.Element {
  return (
    <AppShell>
      <InvoiceEditor draft={null} />
    </AppShell>
  );
}

function EditDocumentPage(): JSX.Element {
  const { documentId } = billingDocumentEditRoute.useParams();
  const detailQuery = useQuery({
    queryKey: ['document', documentId],
    queryFn: () => api.getDocument(documentId),
  });
  const doc = detailQuery.data;

  return (
    <AppShell>
      {detailQuery.isError ? (
        <Alert variant="destructive">
          <AlertTitle>Could not load the draft</AlertTitle>
          <AlertDescription>{toMessage(detailQuery.error)}</AlertDescription>
        </Alert>
      ) : !doc ? (
        <p className="text-sm text-[hsl(var(--muted-foreground))]">Loading…</p>
      ) : doc.status !== 'draft' ? (
        <Alert>
          <AlertTitle>This document is issued</AlertTitle>
          <AlertDescription>
            Issued documents never change. Raise a credit note from the{' '}
            <Link
              to="/billing/documents/$documentId"
              params={{ documentId }}
              className="font-medium underline"
            >
              document page
            </Link>{' '}
            instead.
          </AlertDescription>
        </Alert>
      ) : (
        <InvoiceEditor draft={doc} />
      )}
    </AppShell>
  );
}

function InvoiceEditor({ draft }: { draft: DocumentDetail | null }): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const editing = draft !== null;

  const clientsQuery = useQuery({
    queryKey: ['clients-for-billing'],
    queryFn: () => api.listClients({ status: 'active', pageSize: 100 }),
  });
  const projectsQuery = useQuery({
    queryKey: ['projects-for-billing'],
    queryFn: () => api.listProjects({}),
  });
  const form = useForm<EditorValues>({
    defaultValues: draft ? valuesFromDraft(draft) : emptyValues(),
  });
  const { fields, append, remove } = useFieldArray({ control: form.control, name: 'lines' });
  const watched = form.watch();
  // react-hook-form mutates the lines array in place, so a watcher sees the
  // same reference on every keystroke; snapshotting keeps the totals live.
  const [watchedLines, setWatchedLines] = React.useState<LineForm[]>(() =>
    form.getValues('lines').map((l) => ({ ...l })),
  );
  React.useEffect(() => {
    const subscription = form.watch((_values, info) => {
      if (info.name && !info.name.startsWith('lines')) return;
      setWatchedLines(form.getValues('lines').map((l) => ({ ...l })));
    });
    return () => subscription.unsubscribe();
  }, [form]);

  const clients = clientsQuery.data?.items ?? [];
  const projects = projectsQuery.data ?? [];
  const clientProjects = React.useMemo(
    () => projects.filter((p) => p.clientId === watched.clientId || p.id === watched.projectId),
    [projects, watched.clientId, watched.projectId],
  );

  const clientQuery = useQuery({
    queryKey: ['client', watched.clientId],
    queryFn: () => api.getClient(watched.clientId),
    enabled: watched.clientId !== '',
  });
  const client =
    clientQuery.data && clientQuery.data.id === watched.clientId ? clientQuery.data : null;

  // Picking a customer fills in their GST details and payment terms; a draft
  // being reopened keeps what was saved until the customer is changed.
  const appliedClientId = React.useRef<string>(draft?.clientId ?? '');
  React.useEffect(() => {
    if (!client || client.id === appliedClientId.current) return;
    appliedClientId.current = client.id;
    form.setValue('placeOfSupplyState', client.placeOfSupplyState, { shouldDirty: true });
    const terms = termsForDays(client.paymentTermsDays);
    form.setValue('terms', terms, { shouldDirty: true });
    form.setValue('dueDate', addDays(form.getValues('issueDate'), client.paymentTermsDays), {
      shouldDirty: true,
    });
    const project = form.getValues('projectId');
    if (project && !projects.some((p) => p.id === project && p.clientId === client.id)) {
      form.setValue('projectId', '');
    }
  }, [client, form, projects]);

  React.useEffect(() => {
    const days = daysForTerms(watched.terms);
    if (days === null) return;
    const next = addDays(watched.issueDate, days);
    if (next && next !== form.getValues('dueDate')) form.setValue('dueDate', next);
  }, [watched.terms, watched.issueDate, form]);

  const numberQuery = useQuery({
    queryKey: ['next-document-number', watched.docType, watched.issueDate],
    queryFn: () => api.getNextDocumentNumber(watched.docType, watched.issueDate),
    enabled: /^\d{4}-\d{2}-\d{2}$/.test(watched.issueDate),
  });

  const companyStateCode = numberQuery.data?.companyStateCode ?? null;
  const customNumber =
    watched.number.trim().length > 0 &&
    watched.number.trim() !== (numberQuery.data?.expectedNumber ?? '');
  const numberFormatError =
    customNumber &&
    numberQuery.data &&
    parseDocNumber(numberQuery.data.template, numberQuery.data.fy, watched.number.trim()) === null
      ? `Must follow the format ${numberQuery.data.template}, like ${numberQuery.data.expectedNumber}`
      : undefined;
  const missingProfile = numberQuery.data ? !numberQuery.data.hasCompanyProfile : false;
  const intra = React.useMemo(
    () => isIntraState(watched.placeOfSupplyState || '', companyStateCode),
    [watched.placeOfSupplyState, companyStateCode],
  );
  const preview = React.useMemo(() => {
    try {
      const inputs = watchedLines.map((l) => ({
        description: l.description,
        hsnSac: l.hsnSac || null,
        qty: qtyMilli(Math.max(1, Math.round((l.qtyDecimal || 0) * 1000))),
        ratePaise: paise(Number(rupeesToPaise(l.rateRupees || 0))),
        gstRateBpp: l.gstRateBpp,
      }));
      return computeDocumentTotals(inputs, intra);
    } catch {
      return null;
    }
  }, [watchedLines, intra]);

  const saveMutation = useMutation({
    mutationFn: async (v: EditorValues) => {
      const body = toRequest(v, numberQuery.data?.expectedNumber ?? null);
      if (draft) {
        await api.updateDocument(draft.id, body);
        return { documentId: draft.id };
      }
      return api.createDocument(body);
    },
    onSuccess: async (res) => {
      setErrorMessage(null);
      await queryClient.invalidateQueries({ queryKey: ['documents'] });
      await queryClient.invalidateQueries({ queryKey: ['document', res.documentId] });
      await navigate({
        to: '/billing/documents/$documentId',
        params: { documentId: res.documentId },
      });
    },
    onError: (err) => setErrorMessage(toMessage(err)),
  });

  const showGstReason = watched.docType === 'bill_of_supply';
  const hsnRequired = watched.docType === 'tax_invoice';
  const errors = form.formState.errors;

  return (
    <form
      onSubmit={form.handleSubmit((v) => saveMutation.mutate(v))}
      className="flex flex-col gap-6"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 flex-col gap-1">
          {editing ? (
            <Link
              to="/billing/documents/$documentId"
              params={{ documentId: draft.id }}
              className="text-xs font-medium text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
            >
              ← Back to draft
            </Link>
          ) : (
            <Link
              to="/billing/documents"
              className="text-xs font-medium text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
            >
              ← Documents
            </Link>
          )}
          <h1 className="font-display text-3xl font-semibold tracking-tight md:text-4xl">
            {editing ? 'Edit draft' : 'New invoice'}
          </h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">
            GST is computed as you type. The number is assigned when the invoice is issued.
          </p>
        </div>
        <div className="flex shrink-0 gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => void navigate({ to: '/billing/documents' })}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={saveMutation.isPending}>
            {saveMutation.isPending ? 'Saving…' : editing ? 'Save draft' : 'Save as draft'}
          </Button>
        </div>
      </div>

      {missingProfile ? (
        <Alert variant="destructive">
          <AlertTitle>No company profile is effective on {watched.issueDate}</AlertTitle>
          <AlertDescription>
            Invoices need your GSTIN, address and bank details, which come from the company
            profile. Saving will fail until an Admin adds one under{' '}
            <Link to="/company/profile" className="font-medium underline underline-offset-2">
              Company profile
            </Link>
            .
          </AlertDescription>
        </Alert>
      ) : null}

      <Card>
        <CardContent className="flex flex-col gap-5 pt-6">
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
            <div className="flex flex-col gap-4">
              <Field label="Customer" required error={errors.clientId?.message}>
                <Select {...form.register('clientId', { required: 'Pick a customer' })}>
                  <option value="">Select a customer…</option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.displayName}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Type" required>
                <Select {...form.register('docType')}>
                  {DOC_TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field
                label="Project"
                hint={
                  watched.clientId
                    ? clientProjects.length === 0
                      ? 'This customer has no projects. Leave blank to invoice without one.'
                      : 'Optional. Links the revenue to a project for P&L.'
                    : 'Optional. Pick a customer first to see their projects.'
                }
              >
                <Select {...form.register('projectId')} disabled={!watched.clientId}>
                  <option value="">No project</option>
                  {clientProjects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.code} · {p.name}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>

            <CustomerDetails
              clientId={watched.clientId}
              client={client}
              loading={clientQuery.isFetching && !client}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 border-t border-[hsl(var(--border))] pt-5 sm:grid-cols-2 lg:grid-cols-4">
            <Field
              label="Invoice #"
              error={errors.number?.message ?? numberFormatError}
              hint={
                customNumber ? (
                  <>
                    Custom number. The series still continues at{' '}
                    <span className="font-mono">{numberQuery.data?.expectedNumber ?? '…'}</span>.{' '}
                    <button
                      type="button"
                      className="underline underline-offset-2"
                      onClick={() => form.setValue('number', '', { shouldValidate: true })}
                    >
                      Use next in series
                    </button>
                  </>
                ) : (
                  <>
                    Next in series, assigned when issued. Type a different number to use it for
                    this invoice only.{' '}
                    <Link to="/company/profile" className="underline underline-offset-2">
                      Change format
                    </Link>
                  </>
                )
              }
            >
              <Input
                placeholder={numberQuery.data?.expectedNumber ?? '…'}
                className="font-mono"
                {...form.register('number', {
                  validate: (value) => {
                    const trimmed = value.trim();
                    if (!trimmed || !numberQuery.data) return true;
                    if (trimmed === numberQuery.data.expectedNumber) return true;
                    return parseDocNumber(numberQuery.data.template, numberQuery.data.fy, trimmed) !== null
                      ? true
                      : `Must follow the format ${numberQuery.data.template}, like ${numberQuery.data.expectedNumber}`;
                  },
                })}
              />
            </Field>
            <Field label="Invoice date" required error={errors.issueDate?.message}>
              <Input type="date" {...form.register('issueDate', { required: 'Pick a date' })} />
            </Field>
            <Field label="Terms">
              <Select {...form.register('terms')}>
                {TERMS_OPTIONS.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Due date">
              <Input
                type="date"
                {...form.register('dueDate', {
                  onChange: () => {
                    if (form.getValues('terms') !== 'custom') form.setValue('terms', 'custom');
                  },
                })}
              />
            </Field>
            <Field
              label="Place of supply"
              required
              error={errors.placeOfSupplyState?.message}
              hint={placeOfSupplyHint(watched.placeOfSupplyState)}
            >
              <Input
                placeholder="e.g. 33"
                {...form.register('placeOfSupplyState', { required: 'Enter the state code' })}
              />
            </Field>
            {showGstReason ? (
              <div className="sm:col-span-2 lg:col-span-3">
                <Field label="GST reason" required error={errors.gstReasonCode?.message}>
                  <Select
                    {...form.register('gstReasonCode', {
                      validate: (v, all) =>
                        all.docType !== 'bill_of_supply' || v !== '' || 'Pick a GST reason',
                    })}
                  >
                    <option value="">Select…</option>
                    {GST_REASON_OPTIONS.map((r) => (
                      <option key={r.value} value={r.value}>
                        {r.label}
                      </option>
                    ))}
                  </Select>
                </Field>
              </div>
            ) : null}
          </div>
        </CardContent>
      </Card>

      <Card className="overflow-hidden">
        <CardHeader className="flex-row items-end justify-between gap-4 space-y-0">
          <div className="flex flex-col gap-1">
            <CardTitle>Items</CardTitle>
            <CardDescription>
              {intra ? 'Intra-state supply · CGST + SGST' : 'Inter-state supply · IGST'}
              {hsnRequired ? ' · HSN/SAC required on every line' : ''}
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-sm">
              <thead>
                <tr className="border-y border-[hsl(var(--border))] bg-[hsl(var(--muted))] text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-[hsl(var(--muted-foreground))]">
                  <th className="py-2.5 pl-6 pr-3">Item details</th>
                  <th className="w-28 py-2.5 pr-3 text-right">Quantity</th>
                  <th className="w-36 py-2.5 pr-3 text-right">Rate (₹)</th>
                  <th className="w-28 py-2.5 pr-3">Tax</th>
                  <th className="w-36 py-2.5 pr-3 text-right">Amount</th>
                  <th className="w-12 py-2.5 pr-4" aria-label="Remove" />
                </tr>
              </thead>
              <tbody className="divide-y divide-[hsl(var(--border))]">
                {fields.map((f, i) => {
                  const line = preview?.lines[i];
                  const lineErrors = errors.lines?.[i];
                  return (
                    <tr key={f.id} className="align-top">
                      <td className="py-3 pl-6 pr-3">
                        <div className="flex flex-col gap-2">
                          <Input
                            placeholder="Describe the service or item"
                            aria-invalid={lineErrors?.description ? true : undefined}
                            {...form.register(`lines.${i}.description` as const, {
                              required: true,
                            })}
                          />
                          <Input
                            placeholder={hsnRequired ? 'HSN/SAC (required)' : 'HSN/SAC'}
                            className="h-9 max-w-[220px] font-mono text-xs"
                            {...form.register(`lines.${i}.hsnSac` as const, {
                              validate: (v, all) =>
                                all.docType !== 'tax_invoice' ||
                                v.trim() !== '' ||
                                'HSN/SAC required',
                            })}
                          />
                          {lineErrors?.hsnSac?.message ? (
                            <span className="text-xs text-[hsl(var(--destructive))]">
                              {lineErrors.hsnSac.message}
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td className="py-3 pr-3">
                        <Input
                          type="number"
                          step="0.001"
                          min="0.001"
                          className="text-right"
                          {...form.register(`lines.${i}.qtyDecimal` as const, {
                            valueAsNumber: true,
                            required: true,
                            min: 0.001,
                          })}
                        />
                      </td>
                      <td className="py-3 pr-3">
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          className="text-right"
                          {...form.register(`lines.${i}.rateRupees` as const, {
                            valueAsNumber: true,
                            required: true,
                            min: 0,
                          })}
                        />
                      </td>
                      <td className="py-3 pr-3">
                        <Select
                          {...form.register(`lines.${i}.gstRateBpp` as const, {
                            valueAsNumber: true,
                          })}
                        >
                          {GST_RATE_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </Select>
                      </td>
                      <td className="py-3 pr-3 text-right">
                        <div className="pt-2.5 font-medium tabular-nums">
                          {line ? formatInr(paise(line.taxablePaise)) : '—'}
                        </div>
                        {line && line.lineTotalPaise !== line.taxablePaise ? (
                          <div className="text-xs text-[hsl(var(--muted-foreground))]">
                            {formatInr(paise(line.lineTotalPaise))} with tax
                          </div>
                        ) : null}
                      </td>
                      <td className="py-3 pr-4 text-right">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          aria-label="Remove line"
                          onClick={() => remove(i)}
                          disabled={fields.length <= 1}
                          className="mt-1 h-9 w-9 p-0 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--destructive))]"
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[hsl(var(--border))] px-6 py-3">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => append({ ...EMPTY_LINE })}
            >
              <Plus className="size-4" />
              Add new row
            </Button>
            {errors.lines ? (
              <p className="text-xs text-[hsl(var(--destructive))]">
                Every line needs a description, a quantity and a rate
                {hsnRequired ? ', plus an HSN/SAC code' : ''}.
              </p>
            ) : null}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <Card>
          <CardContent className="flex flex-col gap-4 pt-6">
            <Field label="Customer notes" hint="Printed on the invoice.">
              <Textarea rows={2} className="min-h-0" {...form.register('customerNotes')} />
            </Field>
            <Field label="Terms & conditions" hint="Printed below the bank details.">
              <Textarea rows={4} {...form.register('termsText')} />
            </Field>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex flex-col gap-3 pt-6 text-sm">
            <TotalRow
              label="Sub total"
              value={preview ? formatInr(paise(preview.subtotalPaise)) : '—'}
            />
            {intra ? (
              <>
                <TotalRow
                  label="CGST"
                  value={preview ? formatInr(sum(preview.lines.map((l) => l.cgstPaise))) : '—'}
                />
                <TotalRow
                  label="SGST"
                  value={preview ? formatInr(sum(preview.lines.map((l) => l.sgstPaise))) : '—'}
                />
              </>
            ) : (
              <TotalRow label="IGST" value={preview ? formatInr(paise(preview.taxPaise)) : '—'} />
            )}
            <div className="mt-1 flex items-baseline justify-between border-t border-[hsl(var(--border))] pt-3">
              <span className="font-display text-base font-semibold">Total (₹)</span>
              <span className="font-display text-2xl font-semibold tabular-nums">
                {preview ? formatInr(paise(preview.totalPaise)) : '—'}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      {errorMessage ? (
        <Alert variant="destructive">
          <AlertTitle>{editing ? 'Save failed' : 'Could not create the draft'}</AlertTitle>
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      ) : null}

      <div className="flex justify-end gap-2 border-t border-[hsl(var(--border))] pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={() => void navigate({ to: '/billing/documents' })}
        >
          Cancel
        </Button>
        <Button type="submit" disabled={saveMutation.isPending}>
          {saveMutation.isPending ? 'Saving…' : editing ? 'Save draft' : 'Save as draft'}
        </Button>
      </div>
    </form>
  );
}

function TotalRow({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex items-baseline justify-between">
      <span className="text-[hsl(var(--muted-foreground))]">{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}

function CustomerDetails({
  clientId,
  client,
  loading,
}: {
  clientId: string;
  client: Awaited<ReturnType<typeof api.getClient>> | null;
  loading: boolean;
}): JSX.Element {
  const primary = client?.contacts[0] ?? null;
  return (
    <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/60 p-4">
      <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[hsl(var(--muted-foreground))]">
        Customer GST details
      </p>
      {!clientId ? (
        <p className="mt-3 text-sm text-[hsl(var(--muted-foreground))]">
          Select a customer and their GSTIN, place of supply and payment terms fill in here.
        </p>
      ) : !client ? (
        <p className="mt-3 text-sm text-[hsl(var(--muted-foreground))]">
          {loading ? 'Loading…' : 'Details unavailable.'}
        </p>
      ) : (
        <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
          <DetailItem label="Legal name" value={client.legalName} />
          <DetailItem label="GSTIN" value={client.gstin ?? '—'} mono />
          <DetailItem
            label="GST treatment"
            value={GST_TREATMENT_LABEL[client.gstTreatment] ?? client.gstTreatment}
          />
          <DetailItem label="PAN" value={client.pan ?? '—'} mono />
          <DetailItem
            label="Place of supply"
            value={
              client.placeOfSupplyState
                ? `${client.placeOfSupplyState} · ${placeOfSupplyHint(client.placeOfSupplyState)}`
                : '—'
            }
          />
          <DetailItem
            label="Payment terms"
            value={
              client.paymentTermsDays === 0 ? 'Due on receipt' : `${client.paymentTermsDays} days`
            }
          />
          <div className="col-span-2">
            <DetailItem label="Billing address" value={client.billingAddress || '—'} />
          </div>
          {primary ? (
            <div className="col-span-2">
              <DetailItem
                label="Contact"
                value={[primary.name, primary.email, primary.phone].filter(Boolean).join(' · ')}
              />
            </div>
          ) : null}
        </dl>
      )}
    </div>
  );
}

function DetailItem({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}): JSX.Element {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-[hsl(var(--muted-foreground))]">{label}</dt>
      <dd className={`truncate ${mono ? 'font-mono text-xs' : ''}`} title={value}>
        {value}
      </dd>
    </div>
  );
}
